<script>
  import { setContext } from "svelte";

  import ImageComponent from "$lib/contentTypes/ImageComponent.svelte";
  import Button from "$lib/components/Button.svelte";
  import Bleed from "./Bleed.svelte";
  import Markdown from "$lib/components/Markdown.svelte";
  import { onMount } from "svelte";
  import { getImageOrientation } from "$lib/utilities/components/image-helpers";

  export let images = [];
  export let displayCaption = true;

  let currentSlide = 1;

  function navigateTo(slide) {
    if (slide > 0 && slide <= images.length) {
      currentSlide = slide;
    } else if (slide == 0) {
      currentSlide = images.length;
    } else if (slide > images.length) {
      currentSlide = 1;
    }
    return currentSlide;
  }

  $: slideAnnouncement = `Viewing ${currentSlide} of ${images.length} items`;

  //Once the captions have registered, get the largest height and apply it to all
  //the captions so that the buttons don't jump around.
  function onResize() {
    let captionHeight = 0;

    carousel.querySelectorAll(".caption").forEach((item) => {
      item.style.height = null;
      let height = item.getBoundingClientRect().height;
      captionHeight = height > captionHeight ? height : captionHeight;
    });

    carousel.querySelectorAll(".caption").forEach((item) => {
      item.style.height = `${captionHeight}px`;
    });
  }

  function onKeyPress(event) {
    if (event.key == "ArrowRight") {
      navigateTo(currentSlide + 1);
    } else if (event.key == "ArrowLeft") {
      navigateTo(currentSlide - 1);
    }
  }

  let carousel;

  onMount(() => {
    onResize();

    window.onresize = onResize;
    carousel.onkeydown = onKeyPress;
  });

  setContext("containerized", true);

  const imageClassMap = {
    portrait: "aspect-16/9 w-auto h-[202px] lg:h-[606px]",
    landscape: "aspect-video w-360 lg:w-[1078px] h-auto",
    square: "aspect-square w-[202px] lg:w-[606px] h-auto"
  };
</script>

<div
  bind:this={carousel}
  class="w-full px-0 lg:px-[10%]"
  role="region"
  aria-label={`Carousel. Viewing ${currentSlide} of ${images.length} items. Use the left and right arrow keys to view all images.`}
>
  <div class="w-full items-stretch relative" role="group">
    {#each images as image, index}
      {@const orientation = getImageOrientation(image?.fields.imageAsset)}
      <!-- svelte-ignore a11y-no-noninteractive-tabindex -->
      <div
        class="slide w-full flex flex-col gap-48"
        tabindex={index + 1 !== currentSlide ? -1 : 0}
        aria-hidden={index + 1 !== currentSlide}
        aria-current={index + 1 === currentSlide}
        class:active={index + 1 === currentSlide}
      >
        <Bleed fullWidth>
          <ImageComponent
            data={image}
            containerClass="w-full px-0 overflow-hidden"
            elementClass="object-cover overflow-hidden mx-auto {imageClassMap[orientation]}"
            displayCaption={false}
            responsive
          />
        </Bleed>
      </div>
    {/each}
  </div>

  {#if images.length > 0}
    <div class="w-full flex flex-col pt-48 px-[7%] lg:px-[12%]">
      <div class="grid grid-cols-2 md:grid-cols-12 gap-16">
        <div class="col-span-1 md:col-span-2 order-2 lg:order-1 justify-self-start">
          <Button
            variant="ghost"
            size="base"
            class="!pr-16 !pl-8"
            on:click={() => navigateTo(currentSlide - 1)}
            icon={{ leading: true, type: "chevron-left" }}
            testid="btn-prev"
            aria-label="Previous Slide"
          >
            Previous
          </Button>
        </div>

        <div
          aria-live="polite"
          class="col-span-full md:col-span-8 order-1 md:order-2 flex justify-center place-items-center gap-16 text-size-12"
          data-testid="navigatable-pages-container"
        >
          {slideAnnouncement}
        </div>
        <div class="col-span-1 md:col-span-2 order-3 justify-self-end">
          <Button
            variant="ghost"
            size="base"
            class=""
            on:click={() => navigateTo(currentSlide + 1)}
            icon={{ type: "chevron-right", trailing: true }}
            testid="btn-next"
            aria-label="Next Slide"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  {/if}

  {#if displayCaption}
    {#each images as image, index}
      <!-- svelte-ignore a11y-no-noninteractive-tabindex -->
      <div
        class="slide w-full flex flex-col gap-48 items-stretch relative pt-48"
        tabindex={index + 1 !== currentSlide ? -1 : 0}
        aria-hidden={index + 1 !== currentSlide}
        aria-current={index + 1 === currentSlide}
        class:active={index + 1 === currentSlide}
      >
        <div
          aria-label="caption"
          class="caption text-black text-size-14 leading-6"
          id={`imageCarouselCaption-${index}`}
          data-testid={`imageCarouselCaption-${index}`}
        >
          {#if image.fields.caption}
            <Markdown input={image.fields.caption} class="px-24 border-l-2 border-neutral-300" />
          {/if}
        </div>
      </div>
    {/each}
  {/if}
</div>

<style lang="postcss">
  .active {
    @apply flex visible;
  }

  .slide:not(.active) {
    @apply absolute top-0 left-0 invisible py-0;
  }
</style>
